import type { VoidFunctionComponent } from 'react';
import type Stripe from 'stripe-latest';
import Icon, { icons } from '../../generic/Icon';
import { GreenCheck } from '../../generic';
import { ICON_SIZE } from '../../generic/Icon/icon-constants';
import Flex from '../../generic/Flex';
import { isBusinessPlan, isEnterprisePlan } from './utils';
import styles from './style.module.less';

type FeaturesComponentProps = {
  product: Stripe.Product,
  asList?: boolean,
};

const getFeaturesTitle = (product: Stripe.Product) => {
  if (isBusinessPlan(product)) {
    return 'Everything in Free, plus:';
  }

  if (isEnterprisePlan(product)) {
    return 'Everything in Business, plus:';
  }

  return 'Features';
};

const FeaturesComponent: VoidFunctionComponent<FeaturesComponentProps> = ({
  product,
  asList = true,
}) => {
  if (!product) {
    return null;
  }

  const features = Object.keys(product.metadata)
    .filter(key => /^feature_\d+$/.test(key)) // Only include keys starting with 'feature_' and ending with a number
    .sort((a: string, b: string) => {
      const orderA = parseInt(a.split('_')[1] || '0', 10);
      const orderB = parseInt(b.split('_')[1] || '0', 10);
      return orderA - orderB;
    })
    .map(key => product.metadata[key]);

  return (
    <div className={styles.featuresContainer}>
      <div className={styles.featuresTitle}>
        {getFeaturesTitle(product)}
      </div>
      <Flex direction={asList ? 'column' : 'row'} wrap="wrap" align="flex-start">
        {features.map((feature) => (
          <Flex justify="flex-start" align="flex-start" gap={12} key={feature} grow={0} shrink={0} basis={asList ? 'auto' : '33.3333%'}>
            <GreenCheck size={ICON_SIZE.LG} />
            <div>{feature}</div>
          </Flex>
        ))}
      </Flex>
    </div>
  );
};

export default FeaturesComponent;

export const EnterpriseFeaturesComponent = () => {
  return (
    <div className={styles.featuresContainer}>
      <div className={styles.featuresTitle}>
        Features
      </div>
      <div className={styles.enterpriseFeaturesContainer}>
        <Icon icon={icons.infoCircle} />
        <div>
          Billing and features are handled custom for this account. Contact support with any questions or concerns.
        </div>
      </div>
    </div>
  );
};
